/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import {
	CosmosEnumEdsGradients,
	CosmosEnumSimpleCtaStyle,
	CosmosEnumSimpleCtaVariant,
	SimpleCtaSectionFragment,
} from "@cosmos/hygraph-schema";

import {
	ButtonList,
	CosmosButtonProps,
	CosmosText,
	ResponsiveContainer,
	SectionContainer,
	TitleDescStack,
} from "@components/elements";
import { cva } from "@panda/css";
import { Box, VStack } from "@panda/jsx";
import { token } from "@panda/tokens";
import { CSSProperties } from "react";

type SimpleCtaSectionProps = Omit<SimpleCtaSectionFragment, "__typename" | "id">;

const styled = cva({
	base: {
		"& .title-desc-stack": {
			display: "flex",
			rowGap: "eds.dimension.24",
			"& .heading": {
				textStyle: "heading2XL",
				smDown: {
					textStyle: "headingXL",
				},
			},
			"& .paragraph": {
				textStyle: "paragraphMD",
				smDown: {
					textStyle: "paragraphMD",
				},
			},
			"& a[href]": {
				_hover: {
					color: "eds.text.primary",
				},
			},
			'& a[href="#"]': {
				pointerEvents: "none",
				textDecoration: "none",
				cursor: "default",
			},
		},
	},
	variants: {
		style: {
			colorful: {
				background: "var(--cosmos-gradient)",
				"& .title-desc-stack .heading, & .title-desc-stack .paragraph": {
					"& a": {
						color: "eds.text.onAccent",
					},
				},
				"& .cta-eyebrow, & .title-desc-stack .paragraph": {
					color: "eds.text.onAccent",
				},
			},
			simple: {
				background: "var(--transparent-light-005, rgba(255, 255, 255, 0.05))",
				"& .title-desc-stack .heading, & .title-desc-stack .paragraph": {
					"& a": {
						background: "var(--cosmos-gradient)",
						backgroundClip: "text",
						color: "transparent",
					},
				},
				"& .cta-eyebrow, & .title-desc-stack .paragraph": {
					color: "eds.text.primary",
				},
			},
		},
		variant: {
			leftToRight: {
				"& .simple-cta-stack": {
					display: "flex",
					justifyContent: "start",
					"& .title-desc-stack": {
						"& .heading, & .paragraph": {
							textAlign: "start",
							maxWidth: "600px",
						},
					},
				},
			},
			centered: {
				"& .simple-cta-stack": {
					display: "flex",
					justifyContent: "center",
					"& .title-desc-stack": {
						"& .heading, & .paragraph": {
							textAlign: "center",
							maxW: "840px", //no eds.dimension
						},
					},
				},
			},
		},
	},
});

interface CssVariables extends CSSProperties {
	"--cosmos-gradient"?: string;
}

export const SimpleCtaSection = ({
	style,
	simpleCtaVariant,
	gradient,
	title,
	eyebrow,
	desc,
	buttons,
}: SimpleCtaSectionProps): React.JSX.Element | null => {
	if (!style) style = CosmosEnumSimpleCtaStyle.Colorful;
	if (!simpleCtaVariant) simpleCtaVariant = CosmosEnumSimpleCtaVariant.LeftToRight;
	if (!gradient) gradient = CosmosEnumEdsGradients.Indigo;

	const cosmosButtons: CosmosButtonProps[] = buttons.map((button) => {
		return {
			...button,
			size: "lg",
			variant: button?.variant ?? "contrast",
			iconPosition: button?.iconPosition ?? "end",
		};
	});

	if (!eyebrow && !title?.text && !desc && !cosmosButtons?.length) return null;

	return (
		<Box
			className={`simple-cta-section ${styled({ style, variant: simpleCtaVariant })}`}
			style={{ "--cosmos-gradient": token(`gradients.eds.gradient.${gradient}`) } as CssVariables}
		>
			<ResponsiveContainer>
				<SectionContainer>
					<VStack
						alignItems={simpleCtaVariant === CosmosEnumSimpleCtaVariant.LeftToRight ? "start" : "center"}
						gap="eds.dimension.24"
						className="simple-cta-stack"
					>
						{eyebrow ? <CosmosText variant="headingSM" text={eyebrow || ""} className="cta-eyebrow" /> : null}
						{title?.text || desc ? (
							<TitleDescStack gradient={gradient} title={{ ...title }} description={{ html: desc }} />
						) : null}
						{cosmosButtons?.length ? <ButtonList buttons={cosmosButtons} /> : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</Box>
	);
};
